import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Renderer2,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  inject,
  OnInit,
  viewChild,
  signal,
  model,
} from '@angular/core';
import { NavigationService, VirtualKeyboardButton, VirtualKeyboardLayout } from '@cue/navigation-services';
import {
  cueClose,
  cueDeleteBackspace,
  cueSpacebar,
  cueEnter,
  cueSpecialChars,
  cueMove,
  IconComponent,
} from '@cue/ui/icons';
import Draggable from '@progress/kendo-draggable';
import { translation } from '@cue/translations';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { NAV_STORAGE_KEYS } from '../../storage-keys';

@Component({
  selector: 'cue-virtual-keyboard',
  imports: [IconComponent, TranslocoPipe, NgStyle, NgTemplateOutlet, NgClass],
  templateUrl: './virtual-keyboard.component.html',
  styleUrl: './virtual-keyboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualKeyboardComponent implements AfterViewInit, OnDestroy, OnInit {
  private navigationService = inject(NavigationService);
  private renderer = inject(Renderer2);

  show = model(false);
  keyboardRef = viewChild('keyboardRef', { read: ElementRef });
  dragHandleRef = viewChild('dragHandle', { read: ElementRef });

  private lastActiveElement: HTMLInputElement | null = null;
  private draggableInstance!: Draggable;

  protected keyboardLayout: VirtualKeyboardLayout;
  protected isShiftActive = false;
  protected isCtrlActive = false;
  protected isCapsLockActive = false;
  // protected showVirtualKeyboard: boolean = false;
  topOffset = signal(0);
  leftOffset = signal(0);
  protected translation = translation;

  ICONS = {
    cueClose,
    cueDeleteBackspace,
    cueSpacebar,
    cueEnter,
    cueSpecialChars,
    cueMove,
  };

  ngOnInit() {
    this.keyboardLayout = this.navigationService.getKeyboardLayout(null);
  }

  ngAfterViewInit(): void {
    if (this.dragHandleRef()) {
      this.draggableInstance = new Draggable({
        drag: (e) => {
          this.leftOffset += e.event.movementX;
          this.topOffset += e.event.movementY;
        },
      });

      this.draggableInstance.bindTo(this.dragHandleRef().nativeElement);
    }
  }

  private lastDragX = 0;
  private lastDragY = 0;

  private initDraggable(): void {
    this.draggableInstance = new Draggable({
      press: (e) => {
        this.lastDragX = e.originalEvent.clientX;
        this.lastDragY = e.originalEvent.clientY;
      },
      drag: (e) => {
        const currentX = e.originalEvent.clientX;
        const currentY = e.originalEvent.clientY;

        const deltaX = currentX - this.lastDragX;
        const deltaY = currentY - this.lastDragY;

        this.leftOffset.update((x) => x + deltaX);
        this.topOffset.update((y) => y + deltaY);
        this.lastDragX = currentX;
        this.lastDragY = currentY;
      },
    });

    this.draggableInstance.bindTo(this.dragHandleRef()!.nativeElement);
  }

  ngOnDestroy(): void {
    if (this.draggableInstance) {
      this.draggableInstance.destroy();
    }
  }

  // showHideVirtualKeyboard() {
  //   const wantToShowKeyboard = !this.showVirtualKeyboard;
  //   this.saveVirtualKeyboard(wantToShowKeyboard);
  // }

  saveVirtualKeyboard(wantToShowKeyboard: boolean) {
    this.show.set(wantToShowKeyboard);
    localStorage.setItem(NAV_STORAGE_KEYS.showVirtualKeyboard, wantToShowKeyboard + '');

    if (wantToShowKeyboard) {
      setTimeout(() => {
        if (this.dragHandleRef() && !this.draggableInstance) {
          this.initDraggable();
        }
      });
    } else {
      this.destroyDraggable();
    }
  }

  private destroyDraggable(): void {
    if (this.draggableInstance) {
      this.draggableInstance.destroy();
      this.draggableInstance = undefined!;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const keyboardElement = this.keyboardRef()?.nativeElement;

    const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
    const isInsideKeyboard = keyboardElement?.contains(target);

    if (isInput) {
      this.lastActiveElement = target as HTMLInputElement;
      this.setPosition(this.lastActiveElement);
      this.saveVirtualKeyboard(true);
    } else if (!isInsideKeyboard) {
      this.saveVirtualKeyboard(false);
    }
  }

  protected setPosition(element: HTMLInputElement): void {
    const rect = element.getBoundingClientRect();
    const isPortrait = window.innerHeight > window.innerWidth;

    if (isPortrait) {
      this.topOffset.set(rect.top - 320);
      this.leftOffset.set(window.innerWidth / 2 - 614 / 2);
    } else {
      this.topOffset.set(rect.top - 15);
      this.leftOffset.set(rect.right + 8);
    }

    this.topOffset.set(Math.max(this.topOffset(), 0));
    this.leftOffset.set(Math.max(this.leftOffset(), 0));
  }

  protected isSpecialKeys(key: VirtualKeyboardButton): boolean {
    return key.isEnterFn || key.isCtrlFn || key.isCapsLockFn || key.isShiftFn || key.isDeleteFn || key.isCloseFn;
  }

  protected handleKeyPress(key: VirtualKeyboardButton, event: MouseEvent): void {
    event.preventDefault();
    if (!this.lastActiveElement) return;

    const start = this.lastActiveElement.selectionStart || 0;
    const end = this.lastActiveElement.selectionEnd || 0;
    const value = this.lastActiveElement.value;

    if (this.isSpecialKeys(key)) {
      this.handleSpecialKeyPressed(key);
    } else {
      let character = '';

      if (key.isSpaceFn) {
        character = ' ';
      } else if (this.isCtrlActive) {
        character = key.ctrlValue;
      } else {
        character =
          this.isShiftActive || this.isCapsLockActive
            ? key.shiftValue || key.defaultValue.toUpperCase()
            : key.defaultValue.toLowerCase();
      }

      const newValue = value.slice(0, start) + character + value.slice(end);
      this.renderer.setProperty(this.lastActiveElement, 'value', newValue);
      this.setSelectionRange(this.lastActiveElement, start + 1);

      if (this.isShiftActive) {
        this.isShiftActive = false;
      }
    }

    this.triggerInputEvent(this.lastActiveElement);
  }

  private handleSpecialKeyPressed(key: VirtualKeyboardButton) {
    if (key.isCapsLockFn) {
      this.isCapsLockActive = !this.isCapsLockActive;
    } else if (key.isShiftFn) {
      this.isShiftActive = !this.isShiftActive;
    } else if (key.isDeleteFn) {
      const start = this.lastActiveElement?.selectionStart || 0;
      const end = this.lastActiveElement?.selectionEnd || 0;
      const value = this.lastActiveElement?.value || '';

      if (start > 0 && this.lastActiveElement) {
        const newValue = value.slice(0, start - 1) + value.slice(end);
        this.renderer.setProperty(this.lastActiveElement, 'value', newValue);
        this.setSelectionRange(this.lastActiveElement, start - 1);
      }
    } else if (key.isCtrlFn) {
      this.isCtrlActive = !this.isCtrlActive;
    } else if (key.isEnterFn) {
      this.lastActiveElement?.blur();
    } else if (key.isCloseFn) {
      this.lastActiveElement?.blur();
      this.saveVirtualKeyboard(false);
    }
  }

  private setSelectionRange(element: HTMLInputElement, position: number): void {
    element.selectionStart = element.selectionEnd = position;
  }

  private triggerInputEvent(element: HTMLInputElement): void {
    const event = new Event('input', { bubbles: true });
    element.dispatchEvent(event);
  }
}
